import { IEnhancedNetworkRequest, IEnhancedNetworkResponse } from './types';

export interface IWebSocketConnectorMessage {
  type: 'network-request' | 'network-response' | 'error' | 'ping' | 'pong';
  data: unknown;
  timestamp: number;
  sessionId: string;
}

export interface IWebSocketConnectorConfig {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  enableHeartbeat?: boolean;
  heartbeatInterval?: number;
  sessionId?: string;
}

/**
 * WebSocket 커넥터 - Debug Time Machine 서버와 실시간 통신
 */
export class WebSocketConnector {
  private _config: Required<IWebSocketConnectorConfig>;
  private _ws: WebSocket | null = null;
  private _isConnected: boolean = false;
  private _reconnectAttempts: number = 0;
  private _heartbeatTimer: NodeJS.Timeout | null = null;
  private _reconnectTimer: NodeJS.Timeout | null = null;
  private _messageQueue: IWebSocketConnectorMessage[] = [];
  private _sessionId: string;

  constructor(config: IWebSocketConnectorConfig) {
    this._config = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      enableHeartbeat: true,
      heartbeatInterval: 30000,
      sessionId: this._generateSessionId(),
      ...config,
    };

    this._sessionId = this._config.sessionId;
    console.log(`[WebSocketConnector] Initialized with session: ${this._sessionId}`);
  }

  /**
   * WebSocket 연결 시작
   */
  public async connect(): Promise<void> {
    if (this._isConnected && this._ws) {
      console.log('[WebSocketConnector] Already connected');
      return;
    }

    console.log(`[WebSocketConnector] Connecting to ${this._config.url}...`);

    try {
      this._ws = new WebSocket(this._config.url);
      
      this._ws.onopen = this._onOpen.bind(this);
      this._ws.onclose = this._onClose.bind(this);
      this._ws.onerror = this._onError.bind(this);
      this._ws.onmessage = this._onMessage.bind(this);

      // 연결 완료를 기다림
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        this._ws!.addEventListener('open', () => {
          clearTimeout(timeout);
          resolve();
        });

        this._ws!.addEventListener('error', (error) => {
          clearTimeout(timeout);
          reject(error);
        });
      });
    } catch (error) {
      console.error('[WebSocketConnector] Connection failed:', error);
      throw error;
    }
  }

  /**
   * WebSocket 연결 해제
   */
  public disconnect(): void {
    console.log('[WebSocketConnector] Disconnecting...');
    
    this._clearTimers();
    
    if (this._ws) {
      this._ws.onopen = null;
      this._ws.onclose = null;
      this._ws.onerror = null;
      this._ws.onmessage = null;
      
      if (this._ws.readyState === WebSocket.OPEN) {
        this._ws.close(1000, 'Manual disconnect');
      }
      
      this._ws = null;
    }

    this._isConnected = false;
    this._reconnectAttempts = 0;
    
    console.log('[WebSocketConnector] Disconnected');
  }

  /**
   * 네트워크 요청 전송
   */
  public sendNetworkRequest(request: IEnhancedNetworkRequest): void {
    const message: IWebSocketConnectorMessage = {
      type: 'network-request',
      data: request,
      timestamp: Date.now(),
      sessionId: this._sessionId,
    };

    this._sendMessage(message);
  }

  /**
   * 네트워크 응답 전송
   */
  public sendNetworkResponse(response: IEnhancedNetworkResponse): void {
    const message: IWebSocketConnectorMessage = {
      type: 'network-response',
      data: response,
      timestamp: Date.now(),
      sessionId: this._sessionId,
    };

    this._sendMessage(message);
  }

  /**
   * 에러 전송
   */
  public sendError(error: Error, request?: IEnhancedNetworkRequest): void {
    const message: IWebSocketConnectorMessage = {
      type: 'error',
      data: {
        message: error.message,
        stack: error.stack,
        request: request,
      },
      timestamp: Date.now(),
      sessionId: this._sessionId,
    };

    this._sendMessage(message);
  }

  /**
   * 연결 상태 확인
   */
  public get isConnected(): boolean {
    return this._isConnected && this._ws?.readyState === WebSocket.OPEN;
  }

  /**
   * 세션 ID 가져오기
   */
  public get sessionId(): string {
    return this._sessionId;
  }

  /**
   * 대기 중인 메시지 수
   */
  public get queueSize(): number {
    return this._messageQueue.length;
  }

  /**
   * WebSocket 열림 이벤트
   */
  private _onOpen(event: Event): void {
    console.log('[WebSocketConnector] Connected successfully');
    
    this._isConnected = true;
    this._reconnectAttempts = 0;
    
    // 대기 중인 메시지들 전송
    this._flushMessageQueue();
    
    // 하트비트 시작
    if (this._config.enableHeartbeat) {
      this._startHeartbeat();
    }
  }

  /**
   * WebSocket 닫힘 이벤트
   */
  private _onClose(event: CloseEvent): void {
    console.log(`[WebSocketConnector] Connection closed: ${event.code} ${event.reason}`);
    
    this._isConnected = false;
    this._clearTimers();
    
    // 비정상적인 종료인 경우 재연결 시도
    if (event.code !== 1000 && this._reconnectAttempts < this._config.maxReconnectAttempts) {
      this._scheduleReconnect();
    }
  }

  /**
   * WebSocket 에러 이벤트
   */
  private _onError(event: Event): void {
    console.error('[WebSocketConnector] WebSocket error:', event);
    
    if (!this._isConnected && this._reconnectAttempts < this._config.maxReconnectAttempts) {
      this._scheduleReconnect();
    }
  }

  /**
   * WebSocket 메시지 수신
   */
  private _onMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as IWebSocketConnectorMessage;
      
      if (message.type === 'ping') {
        // Ping에 Pong으로 응답
        this._sendMessage({
          type: 'pong',
          data: message.data,
          timestamp: Date.now(),
          sessionId: this._sessionId,
        });
      }
      // 다른 메시지 타입들은 필요에 따라 처리
    } catch (error) {
      console.warn('[WebSocketConnector] Failed to parse message:', error);
    }
  }

  /**
   * 메시지 전송
   */
  private _sendMessage(message: IWebSocketConnectorMessage): void {
    if (!this.isConnected) {
      // 연결되지 않은 경우 큐에 저장
      this._messageQueue.push(message);
      
      // 큐 크기 제한 (메모리 보호)
      if (this._messageQueue.length > 1000) {
        this._messageQueue = this._messageQueue.slice(-1000);
      }
      
      // 자동 연결 시도
      if (!this._isConnected && this._reconnectAttempts === 0) {
        this._scheduleReconnect();
      }
      
      return;
    }

    try {
      const serialized = JSON.stringify(message);
      this._ws!.send(serialized);
    } catch (error) {
      console.error('[WebSocketConnector] Failed to send message:', error);
      // 전송 실패한 메시지를 큐에 다시 추가
      this._messageQueue.unshift(message);
    }
  }

  /**
   * 대기 중인 메시지들 전송
   */
  private _flushMessageQueue(): void {
    console.log(`[WebSocketConnector] Flushing ${this._messageQueue.length} queued messages`);
    
    const messages = [...this._messageQueue];
    this._messageQueue = [];
    
    messages.forEach(message => {
      this._sendMessage(message);
    });
  }

  /**
   * 재연결 스케줄링
   */
  private _scheduleReconnect(): void {
    if (this._reconnectTimer) {
      return; // 이미 재연결이 스케줄됨
    }

    this._reconnectAttempts++;
    const delay = this._config.reconnectInterval * Math.pow(1.5, this._reconnectAttempts - 1);
    
    console.log(`[WebSocketConnector] Scheduling reconnect attempt ${this._reconnectAttempts}/${this._config.maxReconnectAttempts} in ${delay}ms`);
    
    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null;
      
      try {
        await this.connect();
      } catch (error) {
        console.error('[WebSocketConnector] Reconnect failed:', error);
        
        if (this._reconnectAttempts < this._config.maxReconnectAttempts) {
          this._scheduleReconnect();
        } else {
          console.error('[WebSocketConnector] Max reconnect attempts reached');
        }
      }
    }, delay);
  }

  /**
   * 하트비트 시작
   */
  private _startHeartbeat(): void {
    this._heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this._sendMessage({
          type: 'ping',
          data: { sessionId: this._sessionId },
          timestamp: Date.now(),
          sessionId: this._sessionId,
        });
      }
    }, this._config.heartbeatInterval);
  }

  /**
   * 타이머들 정리
   */
  private _clearTimers(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  /**
   * 세션 ID 생성
   */
  private _generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 상태 정보 가져오기
   */
  public getState() {
    return {
      isConnected: this._isConnected,
      sessionId: this._sessionId,
      queueSize: this._messageQueue.length,
      reconnectAttempts: this._reconnectAttempts,
      websocketState: this._ws?.readyState,
    };
  }

  /**
   * 정리
   */
  public destroy(): void {
    this.disconnect();
    this._messageQueue = [];
  }
}
